import { Typography } from '@breetometer/components';
import { CopyIcon } from 'BreetAssets';
import { Amount, RowSection } from 'BreetComponents';
import {
	copyToClipboard,
	type CURRENCYCODETYPE,
	CURRENCYCONFIG,
	formatCurrencyAmount,
	formatDateTime,
	FXRATE,
	getFxConversionType,
	getSafeObjectField,
	toFixedPrecision,
} from 'BreetHelpers';
import { useCurrencyOutput, useCurrentFxRate } from 'BreetHooks';
import { useMemo } from 'react';

import type { IConversionsProps } from '../DialogWithdrawalHistoryDetails.helpers';
import RowBalance from './RowBalance';
import RowStatusNote from './RowStatusNote';

const Conversions = ({ withdrawalHistoryData }: IConversionsProps) => {
	const currencyOutput = useCurrencyOutput();

	const conversionType = getFxConversionType(
		withdrawalHistoryData.doc.from?.toUpperCase() as CURRENCYCODETYPE,
		withdrawalHistoryData.doc.to?.toUpperCase() as CURRENCYCODETYPE
	);

	const currencyFxRate = useCurrentFxRate({
		conversionType,
		fxRate: withdrawalHistoryData.doc.to === 'usd' ? FXRATE.BUY : FXRATE.DEFAULT,
	});

	const userFiatConfig = getSafeObjectField(
		CURRENCYCONFIG,
		withdrawalHistoryData.doc.to ? (withdrawalHistoryData.doc.to.toUpperCase() as CURRENCYCODETYPE) : 'USD',
		CURRENCYCONFIG.USD
	);

	const defaultAmount = formatCurrencyAmount(
		Number(
			toFixedPrecision(
				userFiatConfig.code === 'GHS'
					? withdrawalHistoryData.doc.amount / withdrawalHistoryData.doc.rate
					: withdrawalHistoryData.doc.rate * withdrawalHistoryData.doc.amount,
				2
			)
		),
		userFiatConfig.code
	);

	const fiatAmount =
		withdrawalHistoryData.doc.rate > 0
			? formatCurrencyAmount(
					Number(
						(
							withdrawalHistoryData.doc.amount /
							(withdrawalHistoryData.doc.rate / withdrawalHistoryData.doc.conversionRate)
						).toFixed(3)
					),
					userFiatConfig.code,
					3
				)
			: 'Error: Invalid rate';

	const amount = withdrawalHistoryData.doc.isBuyCrypto ? fiatAmount : defaultAmount;

	const isSwapFiat = currencyFxRate.symbol.from !== '$' && currencyFxRate.symbol.to !== '$';

	const beforeAfterObj: Record<'swap' | 'buy-crypto' | 'default', { before: number; after: number }> = {
		'swap': {
			before: withdrawalHistoryData.doc.swapBalances?.[currencyFxRate.symbol.from === '₦' ? 'ngnBefore' : 'ghsBefore'] ?? 0,
			after: withdrawalHistoryData.doc.swapBalances?.[currencyFxRate.symbol.from === '₦' ? 'ngnAfter' : 'ghsAfter'] ?? 0,
		},
		'buy-crypto': {
			before: withdrawalHistoryData.doc.fiatBefore,
			after: withdrawalHistoryData.doc.fiatAfter,
		},
		'default': {
			before: withdrawalHistoryData.doc.usdBefore,
			after: withdrawalHistoryData.doc.usdAfter,
		},
	};

	const beforeAfterType = useMemo((): 'swap' | 'buy-crypto' | 'default' => {
		if (isSwapFiat) return 'swap';
		if (withdrawalHistoryData.doc.isBuyCrypto) return 'buy-crypto';
		return 'default';
	}, [isSwapFiat, withdrawalHistoryData.doc.isBuyCrypto]);

	const beforeAfterValues = beforeAfterObj[beforeAfterType];

	const sourceConfig = getSafeObjectField(
		CURRENCYCONFIG,
		withdrawalHistoryData.doc.from ? (withdrawalHistoryData.doc.from.toUpperCase() as CURRENCYCODETYPE) : 'USD',
		CURRENCYCONFIG.USD
	);

	// The stored rate (doc.rate) is quoted the standard way — leading with the non-dollar
	// currency, e.g. ₦1,600 / $ — regardless of the conversion direction. Read the symbols
	// from the transaction's own currencies (never live FX): whichever side isn't USD leads.
	const isSourceDollar = sourceConfig.symbol === CURRENCYCONFIG.USD.symbol;
	const rateLeadSymbol = isSourceDollar ? userFiatConfig.symbol : sourceConfig.symbol;
	const ratePerSymbol = isSourceDollar ? sourceConfig.symbol : userFiatConfig.symbol;

	return (
		<>
			<div className='transactiondetails_header'>
				<Typography as='p'>Conversions</Typography>
				<Typography as='h5'>
					{currencyFxRate.symbol.from}
					<Amount>{currencyOutput(withdrawalHistoryData.amount)}</Amount>
				</Typography>
			</div>

			<div className='transactiondetails_itemscontainer bg-primary-500'>
				<RowSection.Tuple
					leftLabel={`${userFiatConfig.code} Amount`}
					leftComponent={
						<div className='transactiondetails_value'>
							<Typography className='color-neutral-50'>
								<Amount>{amount}</Amount>
							</Typography>
						</div>
					}
					rightLabel='Rate'
					rightComponent={
						<div className='transactiondetails_value'>
							<Typography className='color-neutral-50'>
								{rateLeadSymbol}
								<Amount>{currencyOutput(withdrawalHistoryData.doc.rate)}</Amount> / {ratePerSymbol}
							</Typography>
						</div>
					}
				/>

				<RowSection.Single
					label='Description'
					component={
						<div className='transactiondetails_value'>
							<Typography className='color-neutral-50'>
								<Amount>{withdrawalHistoryData.description}</Amount>
							</Typography>
						</div>
					}
				/>

				<RowSection.Single
					label='Transaction ID'
					component={
						<div className='transactiondetails_value'>
							<Typography className='color-neutral-50'>{withdrawalHistoryData.doc._id}</Typography>
							<CopyIcon
								variant='colored'
								focusable
								aria-label='Copy reference ID'
								role='button'
								tabIndex={0}
								onClick={copyToClipboard(withdrawalHistoryData.doc._id, 'Reference ID copied successfully')}
								className='cursor_pointer hide-during-screenshot'
							/>
						</div>
					}
				/>

				<RowStatusNote status={withdrawalHistoryData.status} />

				<RowBalance
					symbol={currencyFxRate.symbol.from}
					before={currencyOutput(beforeAfterValues.before)}
					after={currencyOutput(beforeAfterValues.after)}
				/>

				<RowSection.Single
					label='Date'
					component={
						<div className='transactiondetails_value'>
							<Typography className='color-neutral-50'>{formatDateTime(withdrawalHistoryData.updatedAt)}</Typography>
						</div>
					}
				/>
			</div>
		</>
	);
};

export default Conversions;
